import { Badge, Box, Button, Dialog, Flex, IconButton, Portal, Table, Text } from '@chakra-ui/react';
import type { NeedEntry } from '@features/positions/needList/MyNeedListPage';
import { formatDate } from 'helpers/dates';
import { memo, useCallback, useMemo, useState } from 'react';
import { LuCheck, LuInbox, LuUndo2, LuX } from 'react-icons/lu';

interface SupplierBid {
  id: string;
  supplierName: string;
  quantityOffered: number;
  pricePerUnit: number;
  currency: string;
  deliveryDate: string;
  status: 'pending' | 'accepted' | 'rejected';
}

interface Props {
  need: NeedEntry | null;
  onClose: () => void;
}

const initialBids: Record<string, SupplierBid[]> = {
  'nl-1': [
    {
      id: 'bid-1',
      supplierName: 'Harvest Foods Co.',
      quantityOffered: 3000,
      pricePerUnit: 2.5,
      currency: 'GHS',
      deliveryDate: '2026-04-12',
      status: 'accepted',
    },
    {
      id: 'bid-2',
      supplierName: 'GreenMill Supplies',
      quantityOffered: 2000,
      pricePerUnit: 2.8,
      currency: 'GHS',
      deliveryDate: '2026-04-14',
      status: 'pending',
    },
  ],
  'nl-2': [
    {
      id: 'bid-3',
      supplierName: 'Mediterranean Imports',
      quantityOffered: 1200,
      pricePerUnit: 12.0,
      currency: 'GHS',
      deliveryDate: '2026-04-08',
      status: 'accepted',
    },
  ],
  'nl-5': [
    {
      id: 'bid-4',
      supplierName: 'CoreMat Supply',
      quantityOffered: 12000,
      pricePerUnit: 0.35,
      currency: 'GHS',
      deliveryDate: '2026-04-13',
      status: 'accepted',
    },
    {
      id: 'bid-5',
      supplierName: 'BuildRight Materials',
      quantityOffered: 8000,
      pricePerUnit: 0.38,
      currency: 'GHS',
      deliveryDate: '2026-04-14',
      status: 'pending',
    },
    {
      id: 'bid-6',
      supplierName: 'QuickCrete Ltd.',
      quantityOffered: 5000,
      pricePerUnit: 0.4,
      currency: 'GHS',
      deliveryDate: '2026-04-15',
      status: 'rejected',
    },
  ],
};

const bidStatusConfig: Record<string, { label: string; colorPalette: string }> = {
  pending: { label: 'Pending', colorPalette: 'yellow' },
  accepted: { label: 'Accepted', colorPalette: 'green' },
  rejected: { label: 'Rejected', colorPalette: 'red' },
};

const needStatusConfig: Record<string, { label: string; colorPalette: string }> = {
  open: { label: 'Open', colorPalette: 'orange' },
  partially_covered: { label: 'Partially covered', colorPalette: 'blue' },
  covered: { label: 'Covered', colorPalette: 'green' },
};

const COLUMN_HEADER_STYLE = {
  border: 'none',
  fontSize: 'xs',
  fontWeight: 'medium',
  color: 'text.secondary',
  px: 3,
  py: 2,
} as const;

const CELL_STYLE = {
  border: 'none',
  px: 3,
  py: 3,
} as const;

// ─── Bid action buttons ─────────────────────────────────────────────────────

interface BidActionsProps {
  bid: SupplierBid;
  onAccept: (id: string) => void;
  onReject: (id: string) => void;
  onRevoke: (id: string) => void;
}

const BidActions = memo(({ bid, onAccept, onReject, onRevoke }: BidActionsProps) => {
  const handleAccept = useCallback(() => {
    onAccept(bid.id);
  }, [onAccept, bid.id]);

  const handleReject = useCallback(() => {
    onReject(bid.id);
  }, [onReject, bid.id]);

  const handleRevoke = useCallback(() => {
    onRevoke(bid.id);
  }, [onRevoke, bid.id]);

  if (bid.status === 'pending') {
    return (
      <Flex gap={1}>
        <IconButton
          aria-label="Accept bid"
          size="2xs"
          variant="ghost"
          borderRadius="md"
          colorPalette="green"
          onClick={handleAccept}
        >
          <LuCheck />
        </IconButton>
        <IconButton
          aria-label="Reject bid"
          size="2xs"
          variant="ghost"
          borderRadius="md"
          colorPalette="red"
          onClick={handleReject}
        >
          <LuX />
        </IconButton>
      </Flex>
    );
  }

  if (bid.status === 'accepted') {
    return (
      <Button size="2xs" variant="subtle" colorPalette="orange" onClick={handleRevoke}>
        <LuUndo2 size={12} />
        Revoke
      </Button>
    );
  }

  return null;
});
BidActions.displayName = 'BidActions';

// ─── Main dialog ─────────────────────────────────────────────────────────────

const NeedDetailDialog = memo(({ need, onClose }: Props) => {
  const [bidState, setBidState] = useState<Record<string, SupplierBid[]>>(initialBids);

  const handleOpenChange = useCallback(
    (e: { open: boolean }) => {
      if (e.open === false) {
        onClose();
      }
    },
    [onClose],
  );

  const bids = useMemo(() => {
    if (need == null) {
      return [];
    }
    return bidState[need.id] ?? [];
  }, [need, bidState]);

  const coveredQuantity = useMemo(() => {
    return bids
      .filter((b) => b.status === 'accepted')
      .reduce((sum, b) => sum + b.quantityOffered, 0);
  }, [bids]);

  const updateBidStatus = useCallback(
    (bidId: string, newStatus: SupplierBid['status']) => {
      if (need == null) {
        return;
      }
      setBidState((prev) => ({
        ...prev,
        [need.id]: (prev[need.id] ?? []).map((b) =>
          b.id === bidId ? { ...b, status: newStatus } : b,
        ),
      }));
    },
    [need],
  );

  const onAcceptBid = useCallback(
    (bidId: string) => {
      updateBidStatus(bidId, 'accepted');
    },
    [updateBidStatus],
  );

  const onRejectBid = useCallback(
    (bidId: string) => {
      updateBidStatus(bidId, 'rejected');
    },
    [updateBidStatus],
  );

  const onRevokeBid = useCallback(
    (bidId: string) => {
      updateBidStatus(bidId, 'pending');
    },
    [updateBidStatus],
  );

  if (need == null) {
    return null;
  }

  const statusCfg = needStatusConfig[need.status];
  const coveragePct = need.quantity > 0 ? Math.round((coveredQuantity / need.quantity) * 100) : 0;

  return (
    <Dialog.Root open={need != null} onOpenChange={handleOpenChange} size={{ base: 'sm', md: 'lg' }}>
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content borderRadius="xl">
            <Dialog.Header px={6} pt={6} pb={0}>
              <Flex alignItems="center" gap={2} flexWrap="wrap">
                <Dialog.Title fontSize="lg">{need.itemName}</Dialog.Title>
                <Badge variant="outline" colorPalette={statusCfg.colorPalette} borderRadius="full" px={2}>
                  {statusCfg.label}
                </Badge>
              </Flex>
            </Dialog.Header>

            <Dialog.Body px={6} py={4} display="flex" flexDirection="column" gap={5}>
              <Flex gap={4} flexWrap="wrap">
                <StatBox label="Quantity needed" value={`${need.quantity.toLocaleString()} ${need.unit}`} />
                <StatBox label="Bids received" value={String(bids.length)} />
                <StatBox label="Coverage" value={`${coveragePct}%`} />
                <StatBox
                  label="Delivery"
                  value={need.deliveryDate != null ? formatDate(need.deliveryDate) : 'Flexible'}
                />
              </Flex>

              <Flex direction="column" gap={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Supplier bids
                </Text>
                {bids.length === 0 ? (
                  <Flex
                    direction="column"
                    alignItems="center"
                    gap={2}
                    py={8}
                    color="text.secondary"
                  >
                    <LuInbox size={24} />
                    <Text fontSize="sm">No bids yet</Text>
                  </Flex>
                ) : (
                  <>
                    {/* Table view — md and up */}
                    <Box
                      display={{ base: 'none', md: 'block' }}
                      borderWidth="1px"
                      borderColor="border.light"
                      borderRadius="lg"
                      overflow="hidden"
                    >
                      <Table.Root size="sm" w="full">
                        <Table.Header>
                          <Table.Row bg="surface.muted">
                            <Table.ColumnHeader {...COLUMN_HEADER_STYLE}>Supplier</Table.ColumnHeader>
                            <Table.ColumnHeader {...COLUMN_HEADER_STYLE}>Qty</Table.ColumnHeader>
                            <Table.ColumnHeader {...COLUMN_HEADER_STYLE}>Price/unit</Table.ColumnHeader>
                            <Table.ColumnHeader {...COLUMN_HEADER_STYLE}>Delivery</Table.ColumnHeader>
                            <Table.ColumnHeader {...COLUMN_HEADER_STYLE}>Status</Table.ColumnHeader>
                            <Table.ColumnHeader {...COLUMN_HEADER_STYLE} />
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {bids.map((bid) => {
                            const cfg = bidStatusConfig[bid.status];
                            return (
                              <Table.Row key={bid.id}>
                                <Table.Cell {...CELL_STYLE}>
                                  <Text fontSize="sm" fontWeight="medium">
                                    {bid.supplierName}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell {...CELL_STYLE}>
                                  <Text fontSize="sm">
                                    {bid.quantityOffered.toLocaleString()}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell {...CELL_STYLE}>
                                  <Text fontSize="sm">
                                    {bid.currency} {bid.pricePerUnit}
                                  </Text>
                                </Table.Cell>
                                <Table.Cell {...CELL_STYLE}>
                                  <Text fontSize="sm">{formatDate(bid.deliveryDate)}</Text>
                                </Table.Cell>
                                <Table.Cell {...CELL_STYLE}>
                                  <Badge
                                    size="sm"
                                    variant="subtle"
                                    colorPalette={cfg.colorPalette}
                                  >
                                    {cfg.label}
                                  </Badge>
                                </Table.Cell>
                                <Table.Cell {...CELL_STYLE}>
                                  <BidActions
                                    bid={bid}
                                    onAccept={onAcceptBid}
                                    onReject={onRejectBid}
                                    onRevoke={onRevokeBid}
                                  />
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>

                    {/* Card view — below md */}
                    <Flex
                      display={{ base: 'flex', md: 'none' }}
                      direction="column"
                      gap={3}
                    >
                      {bids.map((bid) => {
                        const cfg = bidStatusConfig[bid.status];
                        return (
                          <Flex
                            key={bid.id}
                            direction="column"
                            gap={2}
                            borderWidth="1px"
                            borderColor="border.light"
                            borderRadius="lg"
                            p={3}
                          >
                            <Flex justifyContent="space-between" alignItems="center">
                              <Text fontSize="sm" fontWeight="medium">
                                {bid.supplierName}
                              </Text>
                              <Badge size="sm" variant="subtle" colorPalette={cfg.colorPalette}>
                                {cfg.label}
                              </Badge>
                            </Flex>
                            <Text fontSize="sm" color="fg.muted">
                              {bid.quantityOffered.toLocaleString()} @ {bid.currency} {bid.pricePerUnit}
                            </Text>
                            <Flex justifyContent="space-between" alignItems="center">
                              <Text fontSize="xs" color="text.secondary">
                                Delivery by {formatDate(bid.deliveryDate)}
                              </Text>
                              <BidActions
                                bid={bid}
                                onAccept={onAcceptBid}
                                onReject={onRejectBid}
                                onRevoke={onRevokeBid}
                              />
                            </Flex>
                          </Flex>
                        );
                      })}
                    </Flex>
                  </>
                )}
              </Flex>

              <Flex direction="column" gap={2}>
                <Text fontSize="sm" fontWeight="semibold">
                  Details
                </Text>
                <Flex gap={4} flexWrap="wrap">
                  <StatBox label="Source" value={need.source === 'integration' ? 'Datana ERP' : 'Signal'} />
                  <StatBox label="Created" value={formatDate(need.createdAt)} />
                </Flex>
              </Flex>
            </Dialog.Body>

            <Dialog.Footer px={6} pb={6} pt={2} gap={3}>
              <Button variant="outline" size="sm" borderRadius="md" onClick={onClose}>
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});
NeedDetailDialog.displayName = 'NeedDetailDialog';

const StatBox = ({ label, value }: { label: string; value: string }) => (
  <Flex
    direction="column"
    gap={1}
    bg="surface.muted"
    borderRadius="lg"
    px={4}
    py={3}
    flex={1}
    minW="120px"
  >
    <Text fontSize="xs" color="text.secondary">
      {label}
    </Text>
    <Text fontSize="sm" fontWeight="semibold">
      {value}
    </Text>
  </Flex>
);

export default NeedDetailDialog;
